import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import { Button, Card, Col, Row, Spinner } from "react-bootstrap";
import Success360Service from "../../Services/Success360Service";
import AddSuccess360Banner from "./AddSuccess360Banner";
import EditSuccess360Banner from "./EditSuccess360Banner";

export default function MainSuccess360Banner({
  pageTitle = "Success360 Banner",
}) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [editingBanner, setEditingBanner] = useState(null);

  const handleShow = () => setShowAddModal(true);
  const handleClose = () => setShowAddModal(false);
  const handleShowEdit = (banner) => {
    setEditingBanner(banner);
    setShowEditModal(true);
  };
  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditingBanner(null);
  };

  // Fetch all banners
  const getBanners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Success360Service.getSuccess360Banner();
      if (response.data.success) {
        setBanners(response.data.data || []);
      } else {
        toast.error("Failed to fetch banners");
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      toast.error("An error occurred while fetching banners");
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete banner
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this banner?")) {
      return;
    }

    try {
      setDeleting(id);
      const response = await Success360Service.deleteSuccess360Banner(id);
      if (response.data.success) {
        toast.success("Banner deleted successfully");
        getBanners(); // Refresh the list
      } else {
        toast.error(response.data.message || "Failed to delete banner");
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      toast.error("An error occurred while deleting banner");
    } finally {
      setDeleting(null);
    }
  };

  // Handle successful banner creation
  const handleBannerCreated = () => {
    getBanners(); // Refresh the list
  };

  // Handle successful banner update
  const handleBannerUpdated = () => {
    getBanners(); // Refresh the list
  };

  useEffect(() => {
    getBanners();
  }, [getBanners]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4">
        <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
          <div className="flex-grow-1 d-flex justify-content-center">
            <h1
              className="fw-bold text-center mb-0"
              style={{ letterSpacing: "1px" }}
            >
              Success-360 Banners
            </h1>
          </div>
          <div>
            <Button
              variant="primary"
              onClick={handleShow}
              className="py-2 px-4 fs-5 rounded-pill shadow-sm"
              style={{ minWidth: "220px" }}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Add New {pageTitle}
            </Button>
          </div>
        </div>
      </div>

      {banners.length === 0 ? (
        <div className="text-center py-5">
          <h4 className="text-muted mb-3">No banners found</h4>
          <p className="text-muted mb-4">
            Click{" "}
            <span className="fw-semibold">"Add New Success360 Banner"</span> to
            create your first banner.
          </p>
        </div>
      ) : (
        <Row className="g-4">
          {banners.map((banner) => (
            <Col key={banner.id} xs={12} sm={6} md={4} lg={3}>
              <Card
                className="h-100 border-3 border-primary shadow-sm"
                style={{
                  borderRadius: "1.25rem",
                  borderWidth: "3px",
                  borderStyle: "solid",
                  background:
                    "linear-gradient(135deg, #e3f0ff 0%, #f8f9fa 100%)",
                  transition: "transform 0.15s cubic-bezier(.4,2,.6,1)",
                  transform: "scale(1)",
                }}
              >
                <div
                  style={{
                    height: "210px",
                    background:
                      "linear-gradient(135deg, #d0e6ff 0%, #f8f9fa 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderTopLeftRadius: "1.25rem",
                    borderTopRightRadius: "1.25rem",
                    overflow: "hidden",
                    borderBottom: "2px solid #0d6efd",
                  }}
                >
                  <Card.Img
                    variant="top"
                    src={banner.imagePath}
                    alt="Success360 Banner"
                    style={{
                      height: "100%",
                      width: "100%",
                      objectFit: "cover",
                      filter: "contrast(1.08) saturate(1.1)",
                      borderTopLeftRadius: "1.25rem",
                      borderTopRightRadius: "1.25rem",
                    }}
                  />
                </div>
                <Card.Body className="d-flex flex-column p-4">
                  <Card.Text
                    className="fw-bold text-primary-emphasis mb-3"
                    style={{
                      fontSize: "1.1rem",
                      letterSpacing: "0.5px",
                      textShadow: "0 1px 2px #e3f0ff",
                    }}
                  >
                    <i className="bi bi-calendar-event me-2"></i>
                    <b>
                      Created: {new Date(banner.createdAt).toLocaleDateString()}
                    </b>
                  </Card.Text>
                  <div className="mt-auto">
                    <div className="d-flex justify-content-end gap-3">
                      <Button
                        variant="primary"
                        onClick={() => handleShowEdit(banner)}
                        style={{
                          padding: "0.4rem 1rem",
                          fontSize: "1.15rem",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          boxShadow: "0 2px 8px 0 rgba(13,110,253,0.10)",
                          borderRadius: "2rem",
                        }}
                        className="d-flex align-items-center"
                      >
                        <i className="bi bi-pencil-square me-2"></i>
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDelete(banner.id)}
                        disabled={deleting === banner.id}
                        style={{
                          padding: "0.4rem 1rem",
                          fontSize: "1.15rem",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                          boxShadow: "0 2px 8px 0 rgba(220,53,69,0.10)",
                          borderRadius: "2rem",
                        }}
                        className="d-flex align-items-center"
                      >
                        {deleting === banner.id ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-trash me-2"></i>
                            Delete
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <AddSuccess360Banner
        show={showAddModal}
        onHide={handleClose}
        onSuccess={handleBannerCreated}
        pageTitle={pageTitle}
      />

      <EditSuccess360Banner
        show={showEditModal}
        onHide={handleCloseEdit}
        onSuccess={handleBannerUpdated}
        bannerData={editingBanner}
        pageTitle={pageTitle}
      />
    </>
  );
}
